import { Link } from "@/lib/router-compat";
import { periodResetText, periodSuffix } from "@/config/aiChat";
import type { AiChatQuota } from "@/hooks/useAiChatQuota";

type Props = {
  quota: AiChatQuota | null;
  /** Kompakt variant utan bakgrund, används i chattfoten */
  compact?: boolean;
  className?: string;
  /** Anpassad disclaimer-text. Om den anges ersätter den standardfriskrivningen. */
  disclaimer?: string;
};

const defaultDisclaimer = "Elmarknad AI kan göra misstag. Kontrollera alltid priser & villkor på egen hand.";

/**
 * Kortfattad disclaimer som visar kvotstatus och en enkel AI-ansvarsfriskrivning.
 */
const AiQuotaNotice = ({ quota, className = "", disclaimer = defaultDisclaimer }: Props) => {
  if (!quota) return null;

  const base = "text-xs text-muted-foreground";

  const body = () => {
    if (quota.isAdmin) {
      return (
        <span>
          Obegränsat antal frågor. {disclaimer}
        </span>
      );
    }

    if (quota.signedIn) {
      return quota.remaining > 0 ? (
        <span>
          Du har{" "}
          <strong className="font-semibold text-foreground">
            {quota.remaining} av {quota.limit}
          </strong>{" "}
          frågor kvar {periodSuffix(quota.period)}. {periodResetText(quota.period)} {disclaimer}
        </span>
      ) : (
        <span>
          Du har använt dina {quota.limit} frågor {periodSuffix(quota.period)}.{" "}
          {periodResetText(quota.period)} {disclaimer}
        </span>
      );
    }

    return quota.remaining > 0 ? (
      <span>
        Du har{" "}
        <strong className="font-semibold text-foreground">
          {quota.remaining} av {quota.limit}
        </strong>{" "}
        frågor kvar {periodSuffix(quota.period)}.{" "}
        <Link to="/logga-in?tab=signup&source=ai" className="font-medium text-primary hover:underline">
          Skapa konto
        </Link>{" "}
        för att låsa upp fler frågor. {disclaimer}
      </span>
    ) : (
      <span>
        Du har använt dina {quota.limit} frågor {periodSuffix(quota.period)}.{" "}
        <Link to="/logga-in?tab=signup&source=ai" className="font-medium text-primary hover:underline">
          Skapa ett gratis konto
        </Link>{" "}
        för att ställa fler frågor.
      </span>
    );
  };

  return <p className={`${base} ${className}`}>{body()}</p>;
};

export default AiQuotaNotice;
